import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Radio, Loader2, Calendar, RotateCw, UserX, MailWarning, PackagePlus, PhoneOff } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { ehDoSegmento, formatarDocumento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { TRIAGEM_TH, TRIAGEM_TD, TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO, TRIAGEM_CELULA, TRIAGEM_CELULA_FORTE, TRIAGEM_LARGURA_MINIMA_NOME } from '@/components/kanban/triagem/triagemTabela';
import { AcoesTriagem } from '@/components/kanban/triagem/AcoesTriagem';
import { usePortabilidadeExtras, horasDesde, type ExtrasPortabilidade } from '@/hooks/usePortabilidade';

import { useTriagemPortabilidade, type ItemTriagemPortabilidade } from './useTriagemPortabilidade';
import {
  DIAS_UTEIS_DATA_ATRASADA, ErroBilheteDialog, LinhaCanceladaDialog, ReenviarSmsDialog, SeloClienteSemResposta, SeloPreAtivo,
  SeloReguaSms, SeloResultado, SeloSemData, TitularidadeSmsDialog, type AlvoLinha,
} from './PortabilidadeDialogs';
import { diasUteisDesde } from '@/lib/diasUteis';
import { PedidoComplementarDialog } from './PedidoComplementarDialog';
import { linhasDaCotacao, type AlvoComplementar } from './usePedidoComplementar';

/*
 * TRIAGEM SMS ENVIADO, LINHA POR LINHA (Douglas 28/10/2026, maquete v4 aprovada).
 *
 * "a consulta é individual por linha" · "ela tem que ter um botão de apertar o
 *  número da linha e copiar para consultar" · "vamos deixar apenas os botões do
 *  que realmente faz" · "a lógica é consultar todos rapidamente e ter a
 *  tratativa pelos botões".
 *
 * Cada linha é uma PN. A BKO copia a linha, consulta na Claro e aperta o
 * resultado: Agendado (data e hora), SMS sem resposta, Conflito de
 * titularidade, Erro no bilhete ou Linha cancelada. Tirando o Agendado e a
 * linha cancelada, a linha fica na fila: o botão registra a tratativa, manda a
 * linha para o fim e ela volta a pedir consulta em 24 horas.
 *
 * O Pedido complementar voltou em 29/10/2026 (Douglas, Opção B): o conflito
 * que ela já tratou e abriu chamado vai para a Triagem Pedido Complementar,
 * pela mesma janela da Nota Fiscal / Bilhete (`PedidoComplementarDialog`).
 */

interface TriagemPortabilidadeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HORAS_VOLTA = 24;

/* A Razão Social é a única coluna sem largura fixa: a tabela nunca fica menor
   que as outras colunas + o mínimo do nome (`triagemTabela.ts`). */
const LARGURA_FIXA = 128 + 96 + 84 + 148 + 60 + 96 + 250 + 410;

const digitos = (v: string) => v.replace(/\D/g, '');

function linhaFormatada(item: ItemTriagemPortabilidade) {
  const d = digitos(`${item.ddd}${item.linha}`);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return item.linhas_formatadas[0] || '';
}

/** Sem tratativa nas últimas 24 h: é para consultar agora. */
const paraConsultar = (ex?: ExtrasPortabilidade) => {
  const h = horasDesde(ex?.ultima_tratativa?.em);
  return h === null || h >= HORAS_VOLTA;
};

type Dialogo = { tipo: 'sms' | 'titularidade' | 'bilhete' | 'cancelada'; alvo: AlvoLinha; reenvios: number };

function PortabilidadeRow({ item, extras, onAgendar, isAgendando, onAbrir, onComplementar }: {
  item: ItemTriagemPortabilidade;
  extras?: ExtrasPortabilidade;
  onAgendar: (itemId: string, data: string) => void;
  isAgendando: boolean;
  onAbrir: (tipo: Dialogo['tipo'], alvo: AlvoLinha) => void;
  onComplementar: () => void;
}) {
  const { open: openPedido } = usePainelPedido();
  const [dataAgenda, setDataAgenda] = useState(() =>
    item.data_agendamento ? item.data_agendamento.substring(0, 16).replace(' ', 'T') : '');
  // Agenda sem hora não serve: o aviso da véspera e a consulta da operadora
  // dependem do horário (Douglas 24/09/2026).
  const agendaCompleta = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dataAgenda);
  const linha = linhaFormatada(item);
  const alvo: AlvoLinha = {
    itemId: item.item_id, pedidoId: item.pedido_id, linha: linha || 'do pedido',
    razaoSocial: item.razao_social, numeroPedido: item.numero_pedido,
  };
  const consultar = paraConsultar(extras);
  const titularidadeAberta = extras?.resultado?.tipo === 'titularidade';

  return (
    <tr className={`box-border h-[33px] border-b border-border/30 transition-colors hover:bg-muted/30 ${consultar ? '' : 'bg-muted/20'}`}>
      <td className={TRIAGEM_TD}>
        {item.is_first_of_cnpj ? (
          <CopyOnClick value={item.documento || item.cnpj} className="block truncate text-[10px] tabular-nums">
            {formatarDocumento(item.documento || item.cnpj)}
          </CopyOnClick>
        ) : null}
      </td>
      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA_FORTE} title={item.razao_social}>{item.razao_social || '—'}</span>
      </td>
      <td className={TRIAGEM_TD}>
        <button type="button" onClick={() => openPedido(item.pedido_id, 'visao360')}
          className="block max-w-full truncate text-[11px] font-bold tabular-nums text-primary hover:underline"
          title="Abrir o painel do pedido">
          {item.numero_pedido}{item.item_seq ? `-${item.item_seq}` : ''}
        </button>
      </td>
      <td className={TRIAGEM_TD}>
        {item.numero_cotacao_operadora ? (
          <CopyOnClick value={item.numero_cotacao_operadora} className="block truncate text-[10px] tabular-nums">
            {item.numero_cotacao_operadora}
          </CopyOnClick>
        ) : <span className="text-[11px] text-muted-label">—</span>}
      </td>
      <td className={TRIAGEM_TD}>
        {linha ? (
          <CopyOnClick value={digitos(`${item.ddd}${item.linha}`) || linha}
            className="max-w-full rounded-md border border-border bg-card !mx-0 px-1.5 py-0.5 font-display text-[12px] font-semibold tabular-nums text-foreground hover:border-primary/50">
            {linha}
          </CopyOnClick>
        ) : <span className="text-[11px] text-muted-label">—</span>}
      </td>
      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA} title={item.operadora_doadora}>{item.operadora_doadora === '-' ? '—' : item.operadora_doadora}</span>
      </td>
      <td className={TRIAGEM_TD}><SeloPreAtivo extras={extras} /></td>
      <td className={TRIAGEM_TD}>
        <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <SeloResultado extras={extras} />
          <SeloReguaSms extras={extras} /><SeloSemData extras={extras} />
          <SeloClienteSemResposta extras={extras} />
        </span>
      </td>
      <td className={TRIAGEM_TD}>
        <AcoesTriagem
          avancar={
            <>
              <Input
                type="datetime-local"
                value={dataAgenda}
                onChange={(e) => setDataAgenda(e.target.value)}
                className="h-7 w-[150px] flex-shrink-0 px-1 text-xs"
                title="Data e hora da portabilidade que a Claro mostra — as duas são obrigatórias"
              />
              <Button
                type="button" size="icon" variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title={agendaCompleta ? 'Agendado — sai da fila para Portabilidade agendada. O cliente é avisado na véspera ou no dia.' : 'Informe a data e a hora da portabilidade'}
                onClick={() => agendaCompleta && onAgendar(item.item_id, dataAgenda)}
                disabled={isAgendando || !agendaCompleta}
              >
                {isAgendando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
              </Button>
            </>
          }
          desvios={
            <>
              <Button type="button" size="icon" variant="ghost" className={TRIAGEM_BTN}
                title="SMS sem resposta — reenviei pelo forms da Claro. Avisa o cliente desta linha e manda para o fim da fila."
                onClick={() => onAbrir('sms', alvo)}>
                <RotateCw />
              </Button>
              <Button type="button" size="icon" variant="ghost" className={TRIAGEM_BTN}
                title={titularidadeAberta
                  ? 'Conflito de titularidade — registrar como falou com o cliente hoje (obrigatório a cada 24 h)'
                  : 'Conflito de titularidade — manda ao cliente o titular do pedido e pede o certo. Fica nesta fila.'}
                onClick={() => onAbrir('titularidade', alvo)}>
                <UserX />
              </Button>
              <Button type="button" size="icon" variant="ghost" className={TRIAGEM_BTN}
                title="Erro no bilhete — e-mail aos gerentes da Claro pela atendimento@, pedindo a data. Fica aguardando a Claro."
                onClick={() => onAbrir('bilhete', alvo)}>
                <MailWarning />
              </Button>
              <Button type="button" size="icon" variant="ghost" className={TRIAGEM_BTN}
                title="Pedido complementar — tratei o conflito e abri o chamado. A cotação vai para a Triagem Pedido Complementar com o chamado."
                onClick={onComplementar}>
                <PackagePlus />
              </Button>
            </>
          }
          cancelar={
            <Button type="button" size="icon" variant="ghost" className={TRIAGEM_BTN}
              title="Linha cancelada ou desativada na operadora — cancela só esta linha e pergunta ao cliente se quer uma linha nova"
              onClick={() => onAbrir('cancelada', alvo)}>
              <PhoneOff />
            </Button>
          }
        />
      </td>
    </tr>
  );
}

export function TriagemPortabilidadeModal({ isOpen, onClose }: TriagemPortabilidadeModalProps) {
  const { itens: itensTodos, isLoading, refetch, agendarPortabilidade } = useTriagemPortabilidade();
  /* Só a linha clicada fica ocupada; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [complementar, setComplementar] = useState<AlvoComplementar | null>(null);

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). Lei em
     `triagem/segmentoTriagem.ts`: filtro, nunca coluna nova. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );
  const { data: extras } = usePortabilidadeExtras(itens.map(i => i.item_id), isOpen);

  const uniquePedidos = new Set(itens.map(i => i.pedido_id)).size;
  const contagem = itens.reduce((c, i) => {
    const ex = extras?.get(i.item_id);
    if (paraConsultar(ex)) c.consultar++;
    if (ex?.resultado?.tipo === 'titularidade') c.titularidade++;
    if (ex?.resultado?.tipo === 'erro_bilhete' && !ex.resultado.resposta_em) c.claro++;
    return c;
  }, { consultar: 0, titularidade: 0, claro: 0 });
  const semDataAtrasada = new Set(itens
    .filter(i => (diasUteisDesde(extras?.get(i.item_id)?.entrou_coluna_em) ?? 0) >= DIAS_UTEIS_DATA_ATRASADA)
    .map(i => i.pedido_id)).size;

  const fechar = () => setDialogo(null);
  const alvoDe = (tipo: Dialogo['tipo']) => (dialogo?.tipo === tipo ? dialogo.alvo : null);
  const chip = 'whitespace-nowrap rounded-lg border bg-card px-3 py-1 text-[11px]';

  return (
    <>
      <TriagemShell
        isOpen={isOpen}
        onFechar={onClose}
        Icone={Radio}
        titulo="Triagem SMS Enviado · Agendamento"
        subtitulo="Copie a linha, consulte na Claro e aperte o resultado. A linha tratada vai para o fim e volta em 24 horas."
        contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: 0 }}
        cabecalhoExtra={<>
          <span className={`${chip} ${contagem.consultar > 0 ? 'border-primary/40 text-primary' : 'border-border text-foreground'}`}
            title="Linhas sem tratativa nas últimas 24 horas">
            {contagem.consultar} para consultar hoje
          </span>
          {contagem.titularidade > 0 && (
            <span className={`${chip} border-border text-foreground`} title="Linhas com conflito de titularidade esperando o cliente">
              {contagem.titularidade} titularidade
            </span>
          )}
          {contagem.claro > 0 && (
            <span className={`${chip} border-border text-foreground`} title="Erro no bilhete: e-mail mandado, a Claro ainda não respondeu">
              {contagem.claro} aguardando Claro
            </span>
          )}
          {semDataAtrasada > 0 && (
            <span className={`${chip} border-primary/40 text-primary`}
              title="Pedidos em SMS ENVIADO há 3 dias úteis ou mais sem a data da portabilidade">
              {semDataAtrasada} {semDataAtrasada === 1 ? 'pedido' : 'pedidos'} sem data há 3+ dias úteis
            </span>
          )}
        </>}
        segmento={segmento}
        onSegmento={setSegmento}
        sla={null}
        fifo={false}
        legenda={[
          { key: 'agendar', Icone: Calendar, label: 'Agendado (data e hora)' },
          { key: 'sms', Icone: RotateCw, label: 'SMS sem resposta' },
          { key: 'titularidade', Icone: UserX, label: 'Conflito de titularidade' },
          { key: 'bilhete', Icone: MailWarning, label: 'Erro no bilhete' },
          { key: 'complementar', Icone: PackagePlus, label: 'Pedido complementar' },
          { key: 'cancelada', Icone: PhoneOff, label: 'Linha cancelada' },
        ]}
        isLoading={isLoading}
        onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
        vazio={
          itens.length === 0
            ? { titulo: 'Nenhuma linha esperando a data', descricao: 'Todas as linhas do SMS Enviado foram tratadas.' }
            : undefined
        }
      >
        <table className="w-full" style={{ tableLayout: 'fixed', minWidth: LARGURA_FIXA + TRIAGEM_LARGURA_MINIMA_NOME }}>
          <colgroup>
            <col style={{ width: '128px' }} />
            <col />
            <col style={{ width: '96px' }} />
            <col style={{ width: '84px' }} />
            <col style={{ width: '148px' }} />
            <col style={{ width: '60px' }} />
            <col style={{ width: '96px' }} />
            <col style={{ width: '250px' }} />
            <col style={{ width: '410px' }} />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-surface">
            <tr>
              <th className={TRIAGEM_TH}>{rotuloSegmento(segmento)}</th>
              <th className={TRIAGEM_TH}>Razão Social</th>
              <th className={TRIAGEM_TH}>Pedido</th>
              <th className={TRIAGEM_TH}>Cotação</th>
              <th className={TRIAGEM_TH} title="Clique para copiar só os números e consultar na Claro">Linha</th>
              <th className={TRIAGEM_TH}>Oper.</th>
              <th className={TRIAGEM_TH} title="Tempo desde a primeira entrada no Pré-Ativo — não zera ao trocar de fila">Pré-ativo</th>
              <th className={TRIAGEM_TH} title="O último resultado da consulta, a régua automática do SMS e há quantos dias úteis está sem data">Situação</th>
              <th className={TRIAGEM_TH}>Resultado da consulta</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const ex = extras?.get(item.item_id);
              return (
                <PortabilidadeRow
                  key={item.item_id}
                  item={item}
                  extras={ex}
                  onAgendar={(id, data) => {
                    void ocupar(id, () => agendarPortabilidade({ itemId: id, dataAgenda: data })).catch(() => undefined);
                  }}
                  isAgendando={ocupada(item.item_id)}
                  onAbrir={(tipo, alvo) => setDialogo({ tipo, alvo, reenvios: ex?.reenvios ?? 0 })}
                  onComplementar={() => setComplementar({
                    pedidoId: item.pedido_id, numeroPedido: item.numero_pedido, razaoSocial: item.razao_social,
                    cotacao: item.numero_cotacao_operadora, colunaOrigem: 'SMS ENVIADO', origem: 'Triagem SMS Enviado',
                    linhas: linhasDaCotacao(itensTodos, item, linhaFormatada),
                  })}
                />
              );
            })}
          </tbody>
        </table>
      </TriagemShell>
      <ReenviarSmsDialog alvo={alvoDe('sms')} reenvios={dialogo?.reenvios ?? 0} onClose={fechar} />
      <TitularidadeSmsDialog alvo={alvoDe('titularidade')} onClose={fechar} />
      <ErroBilheteDialog alvo={alvoDe('bilhete')} onClose={fechar} />
      <LinhaCanceladaDialog alvo={alvoDe('cancelada')} onClose={() => { fechar(); void refetch(); }} />
      <PedidoComplementarDialog alvo={complementar} onClose={() => setComplementar(null)} />
    </>
  );
}
